import { useCallback, useState } from "react";

import { faCircleNotch, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { times } from "lodash";
import { useDebounceCallback } from "usehooks-ts";

import { DecisionEnvironment } from "src/api/types";
import {
  DateRange,
  DateRangePicker,
} from "src/base-components/DateRangePicker";
import { EnvironmentPill } from "src/base-components/EnvironmentPill";
import { Icon } from "src/base-components/Icon";
import { SearchBox } from "src/base-components/SearchBox";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { DropdownItem } from "src/design-system/Dropdown";
import { EmptyState } from "src/design-system/EmptyState";

type ResourceSearchFilters = {
  environment: DecisionEnvironment;
  searchQuery: string;
  timeWindow?: DateRange;
};

type ResourceSearchItem = {
  label: string;
  key: string;
  isFetching: boolean;
};

type ResourceSearchPaneProps = {
  items: ResourceSearchItem[];
  resourceName: string;
  isLoading: boolean;
  filters: ResourceSearchFilters;
  onChangeFilters: (filters: ResourceSearchFilters) => void;
  onSelect: (item: ResourceSearchItem) => void;
  searchPlaceholder: string;
  isEvent?: boolean;
};

export const ResourceSearchPane: React.FC<ResourceSearchPaneProps> = ({
  items,
  resourceName,
  isLoading = false,
  filters,
  onChangeFilters,
  onSelect,
  searchPlaceholder,
  isEvent = false,
}) => {
  const [searchQuery, setSearchQuery] = useState(filters.searchQuery);
  const _onChangeFilters = useCallback(
    (searchQuery: string) => {
      onChangeFilters({
        ...filters,
        searchQuery,
      });
    },
    [onChangeFilters, filters],
  );
  const debouncedChangeSearchQuery = useDebounceCallback(_onChangeFilters, 250);

  return (
    <div className="flex flex-col">
      <div className="flex gap-2 px-4 py-2">
        <SearchBox
          placeholder={searchPlaceholder}
          value={searchQuery}
          autoFocus
          onChange={(value) => {
            setSearchQuery(value);
            debouncedChangeSearchQuery(value);
          }}
        />
        {isEvent && (
          <div
            className="w-80"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DateRangePicker
              value={filters.timeWindow}
              hidePredefinedDateRange
              usePredefinedDates
              onChange={(value) => {
                onChangeFilters({ ...filters, timeWindow: value });
              }}
            />
          </div>
        )}
      </div>
      <div className="flex justify-between gap-2 px-4 py-2.5">
        <div className="font-inter-semibold-13px text-gray-800">
          {resourceName}
        </div>
        <div>
          <EnvironmentPill
            value={filters.environment}
            onChange={(env) => {
              onChangeFilters({
                ...filters,
                environment: env,
              });
            }}
          />
        </div>
      </div>
      <div className="pb-4">
        <ResourceItemsList
          isEvent={isEvent}
          isLoading={isLoading}
          items={items}
          resourceName={resourceName}
          searchQuery={filters.searchQuery}
          onSelect={onSelect}
        />
      </div>
    </div>
  );
};

const ResourceItemsList: React.FC<{
  isEvent?: boolean;
  isLoading?: boolean;
  items: ResourceSearchItem[];
  searchQuery: string;
  resourceName: string;
  onSelect: (item: ResourceSearchItem) => void;
}> = ({
  items,
  isLoading,
  searchQuery,
  resourceName,
  onSelect,
  isEvent = false,
}) => {
  if (isLoading) {
    return times(5, (i) => <ResourceItem key={i} isLoading />);
  }

  if (items.length === 0) {
    return (
      <EmptyState
        description={
          searchQuery.length > 0
            ? "Try a different search query"
            : `No ${resourceName} records available`
        }
        headline={`No ${resourceName} found`}
        icon={faSearch}
      />
    );
  }

  return items.map((item) => (
    <ResourceItem
      key={item.key}
      isFetching={item.isFetching}
      onClick={() => onSelect(item)}
    >
      <div data-loc={`resource-item-${item.key}`}>
        {isEvent ? `${item.key} - ${item.label}` : item.label}
      </div>
    </ResourceItem>
  ));
};

export const ResourceItem: React.FC<{
  children?: React.ReactNode;
  isLoading?: boolean;
  onClick?: () => void;
  isFetching?: boolean;
}> = ({ children, isLoading = false, onClick, isFetching }) => (
  <DropdownItem onClick={onClick}>
    {isLoading ? <SkeletonPlaceholder height="h-5" width="w-42" /> : children}
    {isFetching && <Icon icon={faCircleNotch} size="xs" spin />}
  </DropdownItem>
);
